interface Evidence {
  value: string
  needsReview: boolean
  reason?: string
  basis?: string
  originChapterId?: string
  addressRelevance?: string
  evidence?: unknown[]
}

interface ReviewRow {
  formality?: string
  formalityAtoB?: string
  formalityBtoA?: string
  aToBEvidence?: Evidence
  bToAEvidence?: Evidence
}

interface ReviewChapter {
  chapterGuid?: string
  chapterDocumentId?: string
  chapterNumber?: number
  rows?: ReviewRow[]
}

// Mirrors the backend's all-row aggregate when an edit refreshes full detail.
// Missing legacy evidence never certifies a register change as supported.
export const isOptionalFormalityReason = (reason?: string): boolean =>
  reason === 'limited_context' || reason === 'social_context'

export function relationshipReviewSummary(chapters: ReviewChapter[]) {
  const attention = new Set<string>()
  const suggested = new Set<string>()
  const deferred = new Set<string>()
  const previous: Record<string, string | undefined> = {}
  let supported = chapters.length > 0
  const ordered = [...chapters].sort((a, b) =>
    (Number(a.chapterNumber) || 0) - (Number(b.chapterNumber) || 0) ||
    (a.chapterGuid || '').localeCompare(b.chapterGuid || ''))
  ordered.forEach((chapter, index) => {
    const cid = chapter.chapterGuid || String(index)
    const sourceCid = chapter.chapterDocumentId || cid
    if (!chapter.rows?.length) supported = false
    chapter.rows?.forEach(row => {
      ;([['aToBEvidence', 'formalityAtoB'], ['bToAEvidence', 'formalityBtoA']] as const).forEach(([direction, field]) => {
        const decision = row[direction]
        const value = row[field] || row.formality
        if (decision?.needsReview) {
          if (!isOptionalFormalityReason(decision.reason)) attention.add(cid)
          else if (decision.addressRelevance === 'not_observed') deferred.add(cid)
          else suggested.add(cid)
        }
        if (!decision || decision.value !== value) supported = false
        if (direction in previous && previous[direction] !== value) {
          if (!decision || decision.needsReview || !['explicit', 'manual', 'approved'].includes(decision.basis || '') ||
              (decision.basis === 'explicit' && (!decision.evidence?.length || decision.originChapterId !== sourceCid))) {
            supported = false
          }
        }
        previous[direction] = value
      })
    })
  })
  return {
    reviewSummaryVersion: 1,
    reviewAttentionChapterCount: attention.size,
    suggestedDefaultChapterCount: suggested.size,
    deferredDefaultChapterCount: deferred.size,
    supportedFormalityVariation: supported,
  }
}
